import { Box, createStyles, Dialog, DialogContent, DialogTitle, makeStyles, Typography } from "@material-ui/core";
import { ChangeEventHandler, useState } from "react";
import CloseIcon from "@material-ui/icons/Close";
import { useFormik } from "formik";
import * as Yup from "yup";
import { FareastButton } from "./OverlayComponentButton";
import { useAppDispatch } from "../reduxhooks";
import { postOrderLabelNamaUpdate } from "../redux/OrderRedux";

const useStyles = makeStyles(() =>
    createStyles({
        dialogBox: {
            borderRadius: 20,
        },
        dialogHeader: {
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
        },
        closeButton: {
            cursor: "pointer",
            fontSize: "20px",
            border: "none",
            background: "none",
        },
        labelBox: {
            display: 'flex',
            alignItems: "center",
            justifyContent: 'space-evenly',
            backgroundColor: '#232323',
            width: '100%',
        },
        inputBox: {
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
            width: "60%",
        },
        errorText: {
            textAlign: "center",
            width: "100%",
            marginLeft: "auto",
            marginRight: "auto",
            opacity: 0.8,
            fontSize: "10px",
        },
        keteranganText: {
            opacity: 0.8,
            texAlign: "left",
            fontSize: "10px",
        },
        checkboxText: {
            fontSize: "12px",
        },
        checkboxSubtext: {
            fontSize: "8px",
            opacity: 0.8,
        },
        keteranganWaktuHabis: {
            fontSize: "9px",
            color: "#898989ff",
            textAlign: "right",
        },
        timerBox: {
            border: "1px solid #7d7d7dff",
            borderRadius: "25px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-evenly",
            gap: 3,
            padding: 3,
            letterSpacing: "0.1px",
        },
        countdown: {
            backgroundColor: "#232323ff",
            fontSize: "10px",
            fontWeight: 500,
            color: "#fefefe",
            borderRadius: "10px",
            padding: "3px 7px .5px 7px",
        },
        keteranganTimer: {
            fontSize: "9px",
            paddingTop: 2,
            opacity: 0.8,
            marginRight: 3,
        },
    })
);

const maxLetters = 7; // Sesuai keperluan

interface LabelNamaInputProps {
    value: string;
    onChange: ChangeEventHandler<HTMLInputElement>;
}

function LabelNamaInput({ value, onChange }: LabelNamaInputProps) {
    const IAmount = (value.match(/I/g) || []).length;
    const slots = maxLetters + IAmount;
    const letters = value.split("").slice(0, slots);

    return (
        <Box display="flex" style={{ gap: 2, position: "relative", cursor: "text", marginTop: 12 }}>
            {[...Array(slots)].map((_, i) => (
                <Box
                    key={i}
                    style={{
                        width: letters[i] === "I" ? 7 : 21,
                        height: 32,
                        borderBottom: "1px solid #888",
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "center",
                        fontSize: "2rem",
                        background: "transparent",
                        position: "relative",
                    }}
                >
                    <Typography style={{ fontSize: "26px", fontWeight: 600, color: 'white' }}>{letters[i] || ""}</Typography>
                    {i === letters.length && letters.length < slots && (
                        <Box
                            style={{
                                position: "absolute",
                                left: "20%",
                                bottom: 5,
                                transform: "translateX(-50%)",
                                width: "1px",
                                height: "72%",
                                background: "black",
                                animation: "blink 1s infinite",
                                zIndex: 1,
                                backgroundColor: '#a8a8a8',
                            }}
                        />
                    )}
                </Box>
            ))}
            <input
                type="text"
                value={value}
                onChange={onChange}
                style={{
                    position: "absolute",
                    opacity: 0,
                    pointerEvents: "auto",
                    width: slots * 25,
                    height: 40,
                }}
                autoFocus
            />
            <style>
                {`
          @keyframes blink {
            0%, 49% { opacity: 1; }
            50%, 100% { opacity: 0; }
          }
        `}
            </style>
        </Box>
    );
}

interface EditLabelDialogProps {
    onClose: () => void;
    open: boolean;
    userSub: string,
    creationDateTime: string,
    changeLabelDeadline: string,
    labelNamaManualArray: string[];
    labelNamaIndex: number | null;
}

export default function EditLabelDialog({ onClose, open, userSub, creationDateTime, changeLabelDeadline, labelNamaManualArray, labelNamaIndex }: EditLabelDialogProps) {
    const classes = useStyles();
    const [errorMessage, setErrorMessage] = useState("");
    const dispatch = useAppDispatch();
    
    const updateLabelNama = async (labelNamaManualArray: string[], defaultLabelNama: string | undefined) => {
        if ((Date.now() > new Date(changeLabelDeadline).getTime()) || !userSub || !creationDateTime) return;
    
        const result = await dispatch(postOrderLabelNamaUpdate({
            userSub: userSub,
            creationDateTime: creationDateTime,
            labelNamaManualArray,
            defaultLabelNama: defaultLabelNama ? defaultLabelNama : undefined,
        }));
    }

    const validationSchema = Yup.object({
        labelNamaInput: Yup.string()
            .required("Nama masih kosong"),
        applyToAll: Yup.boolean(),
        applyToNext: Yup.boolean(),
    });

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            labelNamaInput: labelNamaManualArray[labelNamaIndex || 0],
            applyToAll: false,
            applyToNext: false,
        },
        validationSchema,
        validateOnChange: true,
        onSubmit: async (values) => {
            if (values.labelNamaInput === "" ) {
                return;
            }

            let updatedArray: string[] = [];

            if (values.applyToAll) {
                updatedArray = Array(labelNamaManualArray.length).fill(values.labelNamaInput);
            } else {
                updatedArray = [...labelNamaManualArray];
                updatedArray[labelNamaIndex || 0] = values.labelNamaInput;
            }

            await updateLabelNama(updatedArray, values?.applyToNext ? values.labelNamaInput : undefined);

            onClose();
        },
    });

    const handleClose = () => {
        formik.resetForm();
        setErrorMessage("");
        onClose();
    }

    // labelNamaIndex null artinya tidak ada yang dipilih
    if (labelNamaIndex === null) {
        return (
            <Dialog onClose={onClose} open={open} classes={{ paper: classes.dialogBox }} scroll="body" style={{ letterSpacing: "0.1px" }}>
                <DialogTitle>
                    <Box className={classes.dialogHeader}>
                        <Typography style={{ fontSize: "12px", fontWeight: 600, color: "#898989ff" }}>LABEL NAMA TIDAK DITEMUKAN</Typography>
                        <button onClick={onClose} className={classes.closeButton}>
                            <CloseIcon />
                        </button>
                    </Box>
                </DialogTitle>

                <DialogContent>
                    <Typography style={{ fontSize: '18px', textAlign: 'center' }}>
                        Maaf Label Nama Ini Tidak Ditemukan. Mohon Pilih Label Nama Lainnya.
                    </Typography>
                </DialogContent>
            </Dialog>
        )
    }

    return (
        <Dialog onClose={handleClose} open={open} classes={{ paper: classes.dialogBox }} scroll="body" style={{ letterSpacing: "0.1px" }}>
            <DialogTitle>
                <Box className={classes.dialogHeader}>
                    <Typography style={{ fontSize: "12px", fontWeight: 600, color: "#898989ff" }}>BUAT LABEL NAMA KAMU</Typography>
                    <button onClick={handleClose} className={classes.closeButton}>
                        <CloseIcon />
                    </button>
                </Box>
            </DialogTitle>

            <DialogContent style={{ overflowX: 'hidden' }}>
                <form onSubmit={formik.handleSubmit}>
                    <Box className={classes.labelBox}>
                        <img src='https://studiosclo.uk/legacy/sur_monogram_minimum.svg' style={{width: '10%', filter: 'invert(100%)'}} />
                        <Typography style={{ fontSize: 9, color: 'white' }}>FOR</Typography>
                        <Box className={classes.inputBox}>
                            <LabelNamaInput
                                value={formik.values.labelNamaInput}
                                onChange={(e) => {
                                    let raw = e.target.value.toUpperCase();

                                    if (/[^A-Z]/.test(raw)) {
                                        setErrorMessage("Hanya menerima huruf");
                                    } else {
                                        setErrorMessage("");
                                    }

                                    // Bersihkan dari selain huruf
                                    let onlyLetters = raw.replace(/[^A-Z]/g, "");

                                    // Hitung selain I
                                    let value = "";
                                    let count = 0;
                                    for (let char of onlyLetters) {
                                        if (char !== "I") {
                                            if (count >= maxLetters) break;
                                            count++;
                                            if (count === maxLetters) {
                                                setErrorMessage(`Maksimal ${maxLetters} karakter`);
                                            } else if (errorMessage === `Maksimal ${maxLetters} karakter`) {
                                                setErrorMessage("");
                                            }
                                        }
                                        value += char;
                                    }
                                    
                                    formik.setFieldValue("labelNamaInput", value);
                                    formik.validateField("labelNamaInput");
                                }}
                            />
                            {
                                formik.errors.labelNamaInput || errorMessage ? (
                                    <Typography className={classes.errorText}>
                                        <i>{formik.errors.labelNamaInput || errorMessage}</i>
                                    </Typography>
                                ) : (
                                    <Typography className={classes.errorText} style={{ color: 'transparent' }}>
                                        - {/* Perlu supaya tidak goyang */}
                                    </Typography>
                                )
                            }
                        </Box>
                    </Box>

                    <Box style={{ marginTop: 8, marginBottom: 8 }}>
                        <Typography className={classes.keteranganText}>Maksimal {maxLetters} Karakter Selain Huruf i</Typography>
                        <Typography className={classes.keteranganText}>Otomatis Menggunakan Huruf Kapital</Typography>
                    </Box>

                    {/* Checkbox applyToAll */}
                    <Box display="flex" alignItems="center" mt={2}>
                        <input
                            type="checkbox"
                            id="applyToAllLabel"
                            checked={formik.values.applyToAll}
                            onChange={(e) => {
                                formik.setFieldValue("applyToAll", e.target.checked);
                                if (!e.target.checked) {
                                    formik.setFieldValue("applyToNext", false);
                                }
                            }}
                        />
                        <label htmlFor="applyToAllLabel" style={{ marginLeft: 8, fontSize: 14 }}>
                            <Typography className={classes.checkboxText}>Terapkan untuk semua label pesanan ini</Typography>
                        </label>
                    </Box>

                    {/* Checkbox applyToNext */}
                    <Box display="flex" alignItems="center" mt={2}>
                        <input type="checkbox" id="applyToNextLabel" checked={formik.values.applyToNext} onChange={(e) => formik.setFieldValue("applyToNext", e.target.checked)} />
                        <label htmlFor="applyToNextLabel" style={{ marginLeft: 8, fontSize: 14 }}>
                            <Typography className={classes.checkboxText}>Terapkan untuk label pesanan berikutnya</Typography>
                            <Typography className={classes.checkboxSubtext}>
                                Semua pesanan berikutnya mempunyai label nama <b>{formik.values.labelNamaInput || "yang sama"}</b>
                            </Typography>
                        </label>
                    </Box>

                    <Box mt={2}>
                        <FareastButton 
                            type="submit"
                            buttonMarginLeftRight="0%"
                            buttonMarginTopBottom="10px"
                            style={{ width: '100%' }}
                        >
                            SIMPAN
                        </FareastButton>
                    </Box>
                </form>
            </DialogContent>
        </Dialog>
    );
}